import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import MetaTags from '../components/MetaTags';
import { webConfig } from '../utils/webConfig';
import colors from '../components/theme/colors';
import { useQueryString } from '../hooks/useQueryString';
import { GivelifyLogo } from '../components/icons';
import { ApiHandler } from '../api';
import { HeartLoading } from '../components/HeartLoading';
import { useDonorProfile } from '../api';
import { navigateToHomepage } from '../utils/navigation';
import { Button } from '../components';

export const StyledActivateDiv = styled.div`
    max-width: 500px;
    padding-left: 24px;
    padding-right: 24px;
    margin: 18px auto;
    .givelify-logo {
        display: block;
        margin: auto;
    }
    .heart-loading {
        margin: auto;
        width: 200px;
        height: 200px;
    }
    .failure-box,
    .success-box {
        padding: 16px;
        margin: 18px auto;
        border-radius: 10px;
    }
    .success-box {
        color: ${colors.utilitySuccess};
        border: 1px solid ${colors.utilitySuccess};
        background-color: ${colors.utilitySuccessLightColor};
    }
    .failure-box {
        color: ${colors.utilityError};
        border: 1px solid ${colors.utilityError};
        background-color: ${colors.utilityErrorLightColor};
    }
`;

const unableToActivate =
    'Unable to activate your account, please try generating another link below.';
const error = (
    <>
        We are unable to resend another link at this time. Please contact our{' '}
        <a
            href={webConfig.supportContactUrl}
            rel={'noreferrer'}
        >
            support team
        </a>{' '}
        if the issue persists.
    </>
);

const activateLoadingState = <HeartLoading className={'heart-loading'} />;

const renderResendFailureState = (
    message: React.ReactNode,
    onClick: () => void,
) => (
    <>
        <div className={'failure-box'}>{message}</div>
        <Button
            onClick={onClick}
            className="w-full"
        >
            Send another link
        </Button>
    </>
);

const Activate: React.FC = () => {
    const { donorProfile } = useDonorProfile();
    const isLoggedIn = Boolean(donorProfile);
    const query = useQueryString();
    const expiryTime = query.get('expiry_time') || query.get('expires');
    const hash = query.get('hash');
    const id = query.get('id');
    const email = query.get('email');
    const signature = query.get('signature');
    const [status, setStatus] = useState<
        'loading' | 'success' | 'resend' | 'resendFailure' | 'activationFailure'
    >('loading');

    const handleHomePage = useCallback(() => {
        navigateToHomepage(isLoggedIn);
    }, [isLoggedIn]);

    const activateSuccessState = (
        <>
            <div className={'success-box'}>
                You have successfully activated your account. You may close this
                tab.
            </div>
            <Button
                onClick={handleHomePage}
                className="w-full"
            >
                Go to homepage
            </Button>
        </>
    );

    const resendSuccessState = (
        <>
            <div className={'success-box'}>
                Activation sent! Please check your inbox for an email from
                Givelify and click the link there.
            </div>
            <Button
                onClick={handleHomePage}
                className="w-full"
            >
                Go to homepage
            </Button>
        </>
    );

    const resendEmail = useCallback(async () => {
        setStatus('loading');
        if (!email) {
            setStatus('resendFailure');
            return;
        }
        const apiRequest = await ApiHandler.instance.auth.resendActivateEmail({
            emailAddress: email,
        });
        if (apiRequest.success) {
            setStatus('resend');
        } else {
            setStatus('resendFailure');
        }
    }, [email]);

    const makeActivationCall = useCallback(async () => {
        setStatus('loading');
        const apiRequest = await ApiHandler.instance.auth.activateDonorProfile({
            email,
            expiryTime,
            id,
            signature,
            hash,
        });
        if (apiRequest.success) {
            setStatus('success');
        } else {
            setStatus('activationFailure');
        }
    }, [email, expiryTime, hash, id, signature]);

    useEffect(() => {
        makeActivationCall();
        //call on first load
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <StyledActivateDiv>
            <GivelifyLogo className={'givelify-logo'} />
            <MetaTags
                title={'Activate Account'}
                description={'Activate your account with Givelify'}
            />
            {status === 'loading' && activateLoadingState}
            {status === 'success' && activateSuccessState}
            {status === 'resend' && resendSuccessState}
            {status === 'resendFailure' &&
                renderResendFailureState(error, resendEmail)}
            {status === 'activationFailure' &&
                renderResendFailureState(unableToActivate, resendEmail)}
        </StyledActivateDiv>
    );
};

export default Activate;
